/**
 * GlobalFilterBar.jsx · v2.8.9.6
 * ─────────────────────────────────
 * Barra reutilizable de filtros globales.
 * Se enlaza con `useGlobalFilters` (persistente en localStorage).
 */
import { useState, useEffect } from "react";
import { Filter, X, Search, Building2, MapPin, User, Calendar, AlertTriangle, RotateCcw, Wifi, WifiOff, Cpu, Flag, Layers, Briefcase } from "lucide-react";
import { useGlobalFilters } from "../lib/globalFilters";
import MultiSelect from "./MultiSelect";

export default function GlobalFilterBar({ compact = false, hideFields = [], className = "" }) {
  const { filters, options, setFilter, reset, activeCount } = useGlobalFilters();
  const [qLocal, setQLocal] = useState(filters.q || "");
  const [cpeLocal, setCpeLocal] = useState(filters.cpe || "");

  // Debounce del buscador
  useEffect(() => {
    const t = setTimeout(() => { if ((filters.q || "") !== qLocal) setFilter("q", qLocal); }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line
  }, [qLocal]);
  useEffect(() => { setQLocal(filters.q || ""); }, [filters.q]);

  // Debounce del CPE
  useEffect(() => {
    const t = setTimeout(() => { if ((filters.cpe || "") !== cpeLocal) setFilter("cpe", cpeLocal); }, 400);
    return () => clearTimeout(t);
  // eslint-disable-next-line
  }, [cpeLocal]);
  useEffect(() => { setCpeLocal(filters.cpe || ""); }, [filters.cpe]);

  const show = (k) => !hideFields.includes(k);

  const [openAdvanced, setOpenAdvanced] = useState(false);

  // v3.2.0 · Chips visuales soportan single (legacy) O array (multi)
  const _asList = (v) => Array.isArray(v) ? v : (v ? [v] : []);
  const chips = [];
  _asList(filters.branch_office_id).forEach((bid) => {
    const b = options.branches.find((x) => x.id === bid);
    chips.push({ key: `branch_office_id::${bid}`, k: "branch_office_id", v: bid, label: `Sucursal: ${b?.name || `#${bid}`}`, icon: Building2 });
  });
  _asList(filters.zone_id).forEach((zid) => {
    const z = options.zones.find((x) => x.id === zid);
    chips.push({ key: `zone_id::${zid}`, k: "zone_id", v: zid, label: `Zona: ${z?.name || `#${zid}`}`, icon: MapPin });
  });
  _asList(filters.technician_id).forEach((tid) => {
    const t = options.technicians.find((x) => x.id === tid);
    chips.push({ key: `technician_id::${tid}`, k: "technician_id", v: tid, label: `Técnico: ${t?.name || String(tid).slice(0, 8)}`, icon: User });
  });
  _asList(filters.solution_node_id).forEach((sid) => {   // v101.227.0
    const t = (options.solution_types || []).find((x) => x.id === sid);
    chips.push({ key: `solution_node_id::${sid}`, k: "solution_node_id", v: sid, label: `Tipo: ${t?.name || String(sid).slice(0, 8)}`, icon: Layers });
  });
  _asList(filters.contratista).forEach((c) => {
    chips.push({ key: `contratista::${c}`, k: "contratista", v: c, label: `Contratista: ${c}`, icon: Briefcase });
  });
  _asList(filters.odoo_stage_name).forEach((s) => {
    chips.push({ key: `odoo_stage_name::${s}`, k: "odoo_stage_name", v: s, label: `Odoo: ${s}`, icon: AlertTriangle });
  });
  if (filters.internal_status) chips.push({ key: "internal_status", k: "internal_status", label: `Estado: ${filters.internal_status}`, icon: AlertTriangle });
  if (filters.q) chips.push({ key: "q", k: "q", label: `Buscar: "${filters.q}"`, icon: Search });
  if (filters.cpe) chips.push({ key: "cpe", k: "cpe", label: `CPE: "${filters.cpe}"`, icon: Cpu });
  if (filters.has_gps === "true") chips.push({ key: "has_gps", k: "has_gps", label: "GPS: Con señal", icon: Wifi });
  if (filters.has_gps === "false") chips.push({ key: "has_gps", k: "has_gps", label: "GPS: Sin señal", icon: WifiOff });
  if (filters.created_from) chips.push({ key: "created_from", k: "created_from", label: `Creada ≥ ${filters.created_from}`, icon: Calendar });
  if (filters.created_to) chips.push({ key: "created_to", k: "created_to", label: `Creada ≤ ${filters.created_to}`, icon: Calendar });
  if (filters.scheduled_from) chips.push({ key: "scheduled_from", k: "scheduled_from", label: `Agenda ≥ ${filters.scheduled_from}`, icon: Calendar });
  if (filters.scheduled_to) chips.push({ key: "scheduled_to", k: "scheduled_to", label: `Agenda ≤ ${filters.scheduled_to}`, icon: Calendar });
  if (filters.stage_mismatch) chips.push({ key: "stage_mismatch", k: "stage_mismatch", label: "Discrepancia Odoo↔A2", icon: AlertTriangle });

  const removeChip = (chip) => {
    const cur = filters[chip.k];
    if (Array.isArray(cur) && chip.v !== undefined) {
      setFilter(chip.k, cur.filter((x) => x !== chip.v));
    } else if (chip.k === "stage_mismatch") {
      setFilter(chip.k, false);
    } else {
      setFilter(chip.k, null);
    }
  };

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-3 space-y-2 relative z-[500] ${className}`} data-testid="global-filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0A3747]">
          <Filter size={13} /> Filtros globales
          {activeCount > 0 && (
            <span className="bg-[#E0E621] text-[#0A3747] text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="filters-active-count">
              {activeCount}
            </span>
          )}
        </div>

        {show("q") && (
          <div className="relative flex-1 min-w-[220px]">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={qLocal}
              onChange={(e) => setQLocal(e.target.value)}
              placeholder="OT, cliente o suscripción…"
              className="w-full pl-7 pr-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
              data-testid="filter-q"
            />
          </div>
        )}

        {show("branch") && (
          <MultiSelect
            icon={<Building2 size={12} />}
            placeholder="Todas las sucursales"
            options={options.branches.map((b) => ({
              value: b.id, label: b.name || `#${b.id}`, sublabel: `${b.cnt || 0} OTs`,
            }))}
            selected={Array.isArray(filters.branch_office_id) ? filters.branch_office_id : (filters.branch_office_id ? [filters.branch_office_id] : [])}
            onChange={(arr) => setFilter("branch_office_id", arr)}
            testId="filter-branch"
          />
        )}

        {show("technician") && (
          <MultiSelect
            icon={<User size={12} />}
            placeholder="Todos los técnicos"
            options={options.technicians.slice(0, 200).map((t) => ({
              value: t.id, label: t.name, sublabel: `${t.cnt || 0} OTs`,
            }))}
            selected={Array.isArray(filters.technician_id) ? filters.technician_id : (filters.technician_id ? [filters.technician_id] : [])}
            onChange={(arr) => setFilter("technician_id", arr)}
            testId="filter-tech"
          />
        )}

        {/* v101.227.0 · Tipo de solución (incluye sus hijos) y contratista */}
        {show("solution") && (
          <MultiSelect
            icon={<Layers size={12} />}
            placeholder="Tipos de solución"
            options={(options.solution_types || []).map((t) => ({
              value: t.id, label: t.path || t.name, sublabel: `${t.cnt || 0} OTs`,
            }))}
            selected={Array.isArray(filters.solution_node_id) ? filters.solution_node_id : (filters.solution_node_id ? [filters.solution_node_id] : [])}
            onChange={(arr) => setFilter("solution_node_id", arr)}
            testId="filter-solution"
          />
        )}

        {show("contratista") && (
          <MultiSelect
            icon={<Briefcase size={12} />}
            placeholder="Contratistas"
            options={(options.contratistas || []).map((c) => ({
              value: c.nombre, label: c.nombre, sublabel: c.cnt ? `${c.cnt} OTs` : "perfil de técnicos",
            }))}
            selected={Array.isArray(filters.contratista) ? filters.contratista : (filters.contratista ? [filters.contratista] : [])}
            onChange={(arr) => setFilter("contratista", arr)}
            testId="filter-contratista"
          />
        )}

        {show("stage") && (
          <MultiSelect
            icon={<AlertTriangle size={12} />}
            placeholder="Estados Odoo"
            options={options.odoo_stages.map((s) => ({
              value: s.stage, label: s.stage, sublabel: `${s.cnt} OTs`,
            }))}
            selected={Array.isArray(filters.odoo_stage_name) ? filters.odoo_stage_name : (filters.odoo_stage_name ? [filters.odoo_stage_name] : [])}
            onChange={(arr) => setFilter("odoo_stage_name", arr)}
            testId="filter-stage-odoo"
          />
        )}

        {/* v9.4.0 · CPE/Serie search (migrado desde filtro local) */}
        {show("cpe") && (
          <div className="relative min-w-[160px]">
            <Cpu size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={cpeLocal}
              onChange={(e) => setCpeLocal(e.target.value)}
              placeholder="CPE / Serie…"
              className="w-full pl-7 pr-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
              data-testid="filter-cpe"
            />
          </div>
        )}

        {/* v9.4.0 · GPS filter (migrado desde filtro local) */}
        {show("gps") && (
          <select
            value={filters.has_gps || ""}
            onChange={(e) => setFilter("has_gps", e.target.value || "")}
            className="pl-2 pr-6 py-1.5 border border-gray-300 rounded text-xs bg-white focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
            data-testid="filter-gps"
          >
            <option value="">GPS: Todos</option>
            <option value="true">Con GPS</option>
            <option value="false">Sin GPS</option>
          </select>
        )}

        {/* v97.0.0 · OT sin tipo de actividad de Odoo */}
        {show("no_activity") && (
          <button
            type="button"
            onClick={() => setFilter("no_activity", !filters.no_activity)}
            className={`flex items-center gap-1 px-2 py-1.5 rounded text-xs font-semibold border transition ${
              filters.no_activity ? "bg-red-600 border-red-600 text-white" : "bg-white border-gray-300 text-red-700 hover:bg-red-50"
            }`}
            title="Mostrar solo OT sin tipo de actividad de Odoo"
            data-testid="filter-no-activity"
          >
            <Flag size={12} /> Sin tipo de actividad
          </button>
        )}

        {!compact && (
          <button
            onClick={() => setOpenAdvanced(!openAdvanced)}
            className="text-xs px-2 py-1.5 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-1"
            data-testid="filter-toggle-advanced"
          >
            <Calendar size={11} /> Avanzado {openAdvanced ? "▲" : "▼"}
          </button>
        )}

        {activeCount > 0 && (
          <button
            onClick={reset}
            className="text-xs px-2 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1"
            data-testid="filter-reset"
          >
            <RotateCcw size={11} /> Limpiar
          </button>
        )}
      </div>

      {openAdvanced && (
        <div className="pt-2 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="filter-advanced-panel">          {show("zone") && (
            <MultiSelect
              icon={<MapPin size={12} />} label="Zonas"
              placeholder="Todas las zonas"
              options={options.zones.map((z) => ({ value: z.id, label: z.name || z.id, sublabel: `${z.cnt || 0} OTs` }))}
              selected={Array.isArray(filters.zone_id) ? filters.zone_id : (filters.zone_id ? [filters.zone_id] : [])}
              onChange={(arr) => setFilter("zone_id", arr)}
              testId="filter-zone"
            />
          )}
          {show("internal_status") && (
            <Select
              icon={AlertTriangle} label="Estado A2"
              value={filters.internal_status || ""}
              onChange={(v) => setFilter("internal_status", v || null)}
              options={[
                { id: "", label: "Todos" },
                ...options.internal_statuses.map((s) => ({ id: s, label: s })),
              ]}
              testId="filter-internal-status" fullWidth
            />
          )}
          <DateField label="Creada desde" value={filters.created_from} onChange={(v) => setFilter("created_from", v || null)} testId="filter-created-from" />
          <DateField label="Creada hasta" value={filters.created_to} onChange={(v) => setFilter("created_to", v || null)} testId="filter-created-to" />
          <DateField label="Agendada desde" value={filters.scheduled_from} onChange={(v) => setFilter("scheduled_from", v || null)} testId="filter-scheduled-from" />
          <DateField label="Agendada hasta" value={filters.scheduled_to} onChange={(v) => setFilter("scheduled_to", v || null)} testId="filter-scheduled-to" />
          <label className="col-span-2 flex items-center gap-2 text-xs bg-amber-50 rounded p-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={!!filters.stage_mismatch}
              onChange={(e) => setFilter("stage_mismatch", e.target.checked)}
              data-testid="filter-mismatch"
              className="accent-amber-600"
            />
            <span className="text-amber-900 font-medium">Solo OTs con discrepancia Odoo vs A2</span>
          </label>
        </div>
      )}

      {/* v2.8.9.8 · Chips visuales de filtros activos */}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-100" data-testid="filter-chips">
          {chips.map((chip) => {
            const { key, label, icon: Icon } = chip;
            return (
              <span
                key={key}
                className="inline-flex items-center gap-1.5 bg-[#0A3747] text-white text-[11px] font-medium px-2.5 py-1 rounded-full"
                data-testid={`filter-chip-${key}`}
              >
                <Icon size={11} />
                <span className="max-w-[220px] truncate">{label}</span>
                <button
                  onClick={() => removeChip(chip)}
                  className="ml-0.5 hover:bg-white/20 rounded-full p-0.5"
                  title="Quitar filtro"
                  data-testid={`filter-chip-remove-${key}`}
                >
                  <X size={11} />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Select({ icon: Icon, value, onChange, options, testId, label, fullWidth }) {
  return (
    <label className={`inline-flex flex-col ${fullWidth ? "w-full" : ""}`}>
      {label && <span className="text-[10px] text-gray-500 mb-0.5">{label}</span>}
      <div className="relative">
        {Icon && <Icon size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />}
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${Icon ? "pl-7" : "pl-2"} pr-6 py-1.5 border border-gray-300 rounded text-xs bg-white focus:ring-2 focus:ring-[#E0E621] focus:border-transparent max-w-[220px] truncate`}
          data-testid={testId}
        >
          {options.map((o) => (
            <option key={o.id || "_"} value={o.id}>{o.label}</option>
          ))}
        </select>
      </div>
    </label>
  );
}

function DateField({ label, value, onChange, testId }) {
  return (
    <label className="inline-flex flex-col">
      <span className="text-[10px] text-gray-500 mb-0.5">{label}</span>
      <input
        type="date"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-[#E0E621] focus:border-transparent"
        data-testid={testId}
      />
    </label>
  );
}
